import { useCallback, useEffect, useState } from 'react'
import { BookingWidget } from './BookingWidget'
import type { BookingWidgetConfig, WidgetEventName } from './types'

export interface WidgetAppProps {
  config: BookingWidgetConfig
  /** Imperative open/close from the host page's API calls. */
  openSignal: number
  closeSignal: number
  onEvent: (name: WidgetEventName, payload?: unknown) => void
}

/**
 * Owns open/closed state and the launcher.
 *
 * Kept separate from BookingWidget so the booking flow can be rendered
 * directly — by the hosted page, by the settings preview, and by tests —
 * without dragging a floating button along with it.
 */
export function WidgetApp({ config, openSignal, closeSignal, onEvent }: WidgetAppProps) {
  const [open, setOpen] = useState(false)

  const setOpenAndNotify = useCallback(
    (next: boolean) => {
      setOpen((current) => {
        if (current !== next) onEvent(next ? 'open' : 'close')
        return next
      })
    },
    [onEvent]
  )

  useEffect(() => {
    if (openSignal > 0) setOpenAndNotify(true)
  }, [openSignal, setOpenAndNotify])

  useEffect(() => {
    if (closeSignal > 0) setOpenAndNotify(false)
  }, [closeSignal, setOpenAndNotify])

  // Let the garage use their own button anywhere on the page.
  useEffect(() => {
    if (!config.triggerSelector) return

    const elements = Array.from(document.querySelectorAll<HTMLElement>(config.triggerSelector))
    if (elements.length === 0) return

    const handler = (event: Event) => {
      event.preventDefault()
      setOpenAndNotify(true)
    }

    for (const element of elements) element.addEventListener('click', handler)
    return () => {
      for (const element of elements) element.removeEventListener('click', handler)
    }
  }, [config.triggerSelector, setOpenAndNotify])

  const embedded = config.displayMode === 'inline' || config.displayMode === 'page'

  // A custom trigger or an embedded layout means no floating button.
  const showLauncher =
    config.launcher !== false && !config.triggerSelector && !embedded

  // The launcher is rendered by BookingWidget rather than here: it needs the
  // channel's theme, and it must not appear before bootstrap succeeds — a
  // button that opens an empty widget is worse than no button.
  return (
    <BookingWidget
      config={config}
      open={open || embedded}
      onOpenChange={setOpenAndNotify}
      onEvent={onEvent}
      showLauncher={showLauncher}
    />
  )
}
